import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, ShieldCheck, Mail, Loader2 } from 'lucide-react';
import { LogoFull } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArcoPublic() {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(null); // { reference }
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    request_type: '', requester_name: '', requester_email: '',
    requester_rut: '', requester_phone: '', description: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/arco/${slug}`);
        setOrg(data);
      } catch (_e) {
        setOrg(false);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request_type || !form.requester_name || !form.requester_email || form.description.length < 10) {
      return toast.error('Complete los campos obligatorios');
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/arco/${slug}/submit`, form);
      setSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar la solicitud');
    } finally { setSending(false); }
  };

  if (loading) return <FullPage><Loader2 className="animate-spin text-[#143A34]" size={22} /></FullPage>;
  if (org === false) return <FullPage><div className="text-[#DC2626]">Portal no encontrado.</div></FullPage>;

  if (submitted) {
    return (
      <PublicShell org={org}>
        <div className="border border-[#A7F3D0] bg-[#ECFDF5] p-8 text-center" data-testid="arco-submitted">
          <CheckCircle2 size={36} className="mx-auto text-[#065F46]" strokeWidth={1.5} />
          <div className="mt-4 text-[18px] font-semibold text-[#065F46]">Solicitud enviada</div>
          <p className="mt-2 text-[13.5px] text-[#065F46] max-w-md mx-auto">
            Le enviamos un correo a <b>{form.requester_email}</b> con un enlace para confirmar su identidad. Debe hacer clic en las próximas 48 horas.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#A7F3D0] rounded text-[12px] font-mono text-[#065F46]">
            <Mail size={12} /> Referencia: {submitted.reference}
          </div>
          <p className="mt-4 text-[12px] text-[#6B7280]">
            Una vez confirmado, {org.razon_social} tiene <b>15 días hábiles</b> para responder, conforme a la Ley N.º 21.719.
          </p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell org={org}>
      <form onSubmit={submit} className="space-y-5" data-testid="arco-form">
        <div>
          <label className="block text-[12.5px] font-medium text-[#1C2329] mb-1.5">¿Qué derecho desea ejercer? *</label>
          <div className="grid grid-cols-1 gap-2">
            {Object.entries(org.types).map(([k, label]) => {
              const active = form.request_type === k;
              return (
                <button key={k} type="button"
                  onClick={() => setForm((f) => ({ ...f, request_type: k }))}
                  className={`text-left border p-3 transition-colors ${active ? 'border-[#143A34] bg-[#F0FDF4]' : 'border-[#E5E7EB] hover:border-[#C7D2CE]'}`}
                  data-testid={`arco-type-${k}`}>
                  <div className="text-[13.5px] font-medium text-[#1C2329]">{label}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Nombre completo *">
            <input className="pv-input" value={form.requester_name}
              onChange={(e) => setForm((f) => ({ ...f, requester_name: e.target.value }))}
              placeholder="Ej: María Pérez González" data-testid="arco-name" />
          </F>
          <F label="Correo electrónico *">
            <input type="email" className="pv-input" value={form.requester_email}
              onChange={(e) => setForm((f) => ({ ...f, requester_email: e.target.value }))}
              placeholder="usted@correo.cl" data-testid="arco-email" />
          </F>
          <F label="RUT (opcional)">
            <input className="pv-input" value={form.requester_rut}
              onChange={(e) => setForm((f) => ({ ...f, requester_rut: e.target.value }))}
              placeholder="12.345.678-9" />
          </F>
          <F label="Teléfono (opcional)">
            <input className="pv-input" value={form.requester_phone}
              onChange={(e) => setForm((f) => ({ ...f, requester_phone: e.target.value }))}
              placeholder="+56 9 ..." />
          </F>
        </div>

        <F label="Describa su solicitud *">
          <textarea className="pv-input" rows={5}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Ej: Solicito conocer todos los datos personales que ustedes tienen sobre mí, la finalidad y con quiénes los comparten."
            data-testid="arco-description" />
        </F>

        <div className="border border-[#E5E7EB] bg-[#FAFBFC] p-3 text-[11.5px] text-[#6B7280] flex items-start gap-2">
          <ShieldCheck size={13} className="mt-0.5 shrink-0 text-[#143A34]" />
          <span>Al enviar, autoriza a {org.razon_social} a tratar los datos aquí ingresados solo para atender esta solicitud. Recibirá un correo para confirmar su identidad. Amparo legal: Ley N.º 21.719, arts. 5 al 12.</span>
        </div>

        <button type="submit" disabled={sending}
          className="w-full py-3 bg-[#143A34] text-white font-medium hover:bg-[#0F2A26] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="arco-submit">
          {sending ? <><Loader2 size={14} className="animate-spin" /> Enviando…</> : 'Enviar solicitud'}
        </button>
      </form>
    </PublicShell>
  );
}

function PublicShell({ org, children }) {
  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-white px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        {org.logo_url ? (
          <img src={org.logo_url} alt={org.razon_social} className="h-10 max-w-[180px] object-contain" />
        ) : (
          <div className="text-[15px] font-semibold text-[#1C2329]">{org.razon_social}</div>
        )}
        <div className="text-[10.5px] text-[#6B7280] uppercase tracking-wider">Portal de derechos · Ley N.º 21.719</div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-[26px] font-semibold text-[#1C2329]">{org.razon_social}</h1>
          <p className="text-[13.5px] text-[#6B7280] mt-2 leading-relaxed">
            Ejerza aquí sus derechos de <b>acceso, rectificación, cancelación, oposición y portabilidad</b> sobre sus datos personales. {org.razon_social} responderá dentro de 15 días hábiles.
          </p>
        </div>
        <div className="bg-white border border-[#E5E7EB] p-6 shadow-sm">{children}</div>
        <div className="mt-6 text-center text-[11px] text-[#9CA3AF]">
          Portal impulsado por <b>Priviax</b> · Cumplimiento Ley 21.719
        </div>
      </div>
    </div>
  );
}

function FullPage({ children }) {
  return <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">{children}</div>;
}

function F({ label, children }) {
  return <div><label className="block text-[12.5px] font-medium text-[#1C2329] mb-1.5">{label}</label>{children}</div>;
}

// -------- Verification page (separado como named export para App.js) --------
export function ArcoVerify() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/arco/verify/${token}`);
        setState({ loading: false, ok: true, data });
      } catch (e) {
        setState({ loading: false, ok: false, error: e?.response?.data?.detail || 'Enlace inválido' });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-[#E5E7EB] p-8 text-center">
        {state.loading ? (
          <><Loader2 size={22} className="mx-auto animate-spin text-[#143A34]" /><div className="mt-3 text-[13px] text-[#6B7280]">Verificando…</div></>
        ) : state.ok ? (
          <>
            <CheckCircle2 size={40} className="mx-auto text-[#065F46]" strokeWidth={1.5} />
            <div className="mt-3 text-[18px] font-semibold text-[#065F46]">Identidad confirmada</div>
            <p className="mt-2 text-[13px] text-[#374151]">
              <b>{state.data.organization}</b> ya está gestionando su solicitud. Le responderán en máximo 15 días hábiles al correo que registró.
            </p>
            <div className="mt-4 text-[11.5px] text-[#6B7280] font-mono">Referencia: {state.data.reference}</div>
          </>
        ) : (
          <>
            <div className="text-[16px] font-semibold text-[#DC2626]">No pudimos verificar el enlace</div>
            <p className="mt-2 text-[13px] text-[#6B7280]">{state.error}</p>
          </>
        )}
      </div>
    </div>
  );
}
